import { FetchOptions, isPerpMarket, Market, PerpMarket } from "@decibeltrade/sdk";
import {
  FetchQueryOptions,
  isServer,
  QueryClient,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { useMemo } from "react";

import { HIDDEN_MARKET_ADDRS } from "@/constants/feature-flags";
import { addressComparisonKey } from "@/utils/address";

import { readDexSdk } from "../dex-sdk";
import { useAppHealthState } from "../health";

export const MARKET_QUERY_KEY = "markets";

export function marketsQuery(queryClient: QueryClient, fetchOptions?: FetchOptions) {
  return {
    queryKey: [MARKET_QUERY_KEY],
    queryFn: async () => {
      const rawMarkets = await readDexSdk.markets.getAll({ includeSpot: true, fetchOptions });

      // Sort markets to prioritize BTC, APT, ETH, SOL at the top and BUBBLE/USD, MINUTE/USD at the bottom
      const priorityOrder = ["BTC/USD", "APT/USD", "ETH/USD", "SOL/USD"];
      const bottomOrder = ["BUBBLE/USD", "MINUTE/USD"];

      const sortedMarkets = [...rawMarkets].sort((a, b) => {
        const aIndex = priorityOrder.indexOf(a.market_name);
        const bIndex = priorityOrder.indexOf(b.market_name);
        const aBottomIndex = bottomOrder.indexOf(a.market_name);
        const bBottomIndex = bottomOrder.indexOf(b.market_name);

        // If both are priority markets, sort by priority order
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }

        // If both are bottom markets, sort by bottom order
        if (aBottomIndex !== -1 && bBottomIndex !== -1) {
          return aBottomIndex - bBottomIndex;
        }

        // Priority markets come first
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;

        // Bottom markets come last
        if (aBottomIndex !== -1) return 1;
        if (bBottomIndex !== -1) return -1;

        // For non-priority, non-bottom markets, maintain original order
        return 0;
      });

      if (!isServer) {
        const { isMissingMarkets, setIsMissingMarkets } = useAppHealthState.getState();
        if (isMissingMarkets) {
          setIsMissingMarkets(false);
        }
      }

      return sortedMarkets;
    },
    retry: (failureCount) => {
      if (isServer) return false;
      const shouldRetry = failureCount < 3;
      if (!shouldRetry) {
        const existingData = queryClient.getQueryData<Market[]>([MARKET_QUERY_KEY]);
        if (!existingData) {
          // Surface the full-screen error so TradePageContent short-circuits
          // before useMarkets/useCurrentMarket can throw to the global error
          // boundary. The screen reads isOnline to choose offline vs outage
          // copy.
          useAppHealthState.getState().setIsMissingMarkets(true);
        }
      }
      return shouldRetry;
    },
  } satisfies FetchQueryOptions;
}

// Module-level so React Query can memoize the select result between renders.
const selectPerpMarkets = (markets: Market[]): PerpMarket[] => markets.filter(isPerpMarket);

/**
 * Perp markets only — the pre-spot behavior. Everything that reasons about
 * positions, leverage, funding, or perp order flow should keep using this.
 */
export function useMarkets() {
  const queryClient = useQueryClient();
  return useSuspenseQuery({ ...marketsQuery(queryClient), select: selectPerpMarkets });
}

/** Perp + spot union, for product-aware surfaces (market selector, routing). */
export function useAllMarkets() {
  const queryClient = useQueryClient();
  return useSuspenseQuery(marketsQuery(queryClient));
}

const ALL_MARKETS_WITH_SPOT_QUERY_KEY = "marketsIncludingSpot";

/**
 * Non-suspending variant, for screens where the market list decorates the page rather than
 * being the page — suspending those would blank out content that has nothing to do with markets.
 */
export function useMarketsQuery() {
  const queryClient = useQueryClient();
  return useQuery(marketsQuery(queryClient));
}

/**
 * The names of the perps `HIDDEN_MARKET_ADDRS` hides, for surfaces that key perps by name.
 * Perp only: a spot can share a perp's name, so spot is filtered by its own address.
 * Suspends until the catalogue lands, so a hidden market never flashes in.
 */
export function useHiddenPerpMarketNames(): ReadonlySet<string> {
  const { data } = useMarkets();
  return useMemo(
    () =>
      new Set(
        data
          .filter((m) => HIDDEN_MARKET_ADDRS.has(addressComparisonKey(m.market_addr)))
          .map((m) => m.market_name),
      ),
    [data],
  );
}

export async function getMarketByAddress(queryClient: QueryClient, address: string) {
  const markets =
    queryClient.getQueryData<Market[]>([MARKET_QUERY_KEY]) ??
    (await queryClient.fetchQuery(marketsQuery(queryClient)));
  const wanted = addressComparisonKey(address);
  const perpMarket = markets?.find((market) => addressComparisonKey(market.market_addr) === wanted);
  if (perpMarket) return perpMarket;
  // Spot fallback: notifications and order updates carry spot market
  // addresses, but the main market list deliberately excludes spot (spot
  // rows would render as 0-leverage perps in selectors). Resolve them from
  // a separate cached spot-inclusive list instead.
  const allMarkets = await queryClient.fetchQuery({
    queryKey: [ALL_MARKETS_WITH_SPOT_QUERY_KEY],
    queryFn: () => readDexSdk.markets.getAll({ includeSpot: true }),
    staleTime: 60_000,
  });
  return allMarkets.find((market) => addressComparisonKey(market.market_addr) === wanted);
}
